import React, { MouseEvent, useCallback, useMemo } from "react";

import { Dispatch, MousePosition, Range } from "../types";

type Props = Readonly<{
  dispatch: Dispatch;
  gutterWidth: number;
  height: number;
  pageRanges: ReadonlyArray<Range>;
  scale: number;
}>;

const computePositionOnElement = (e: MouseEvent) => {
  const rect = e.currentTarget.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;
  return [x, y];
};

const computeMousePosition = (
  e: MouseEvent,
  gutterWidth: number,
  pageRanges: ReadonlyArray<Range>,
  scale: number,
): MousePosition => {
  const [x, y] = computePositionOnElement(e);
  if (
    x < gutterWidth ||
    x > e.currentTarget.clientWidth - gutterWidth ||
    y < gutterWidth ||
    y > e.currentTarget.clientHeight - gutterWidth
  ) {
    return {
      x,
      y,
    };
  }
  for (let i = 0; i < pageRanges.length; i++) {
    const range = pageRanges[i];
    if (y >= range.start && y <= range.end) {
      return {
        page: i,
        x: (x - gutterWidth) / scale,
        y: (y - range.start) / scale,
      };
    }
    if (y < range.start) {
      return {
        x,
        y,
      };
    }
  }
  return {
    x,
    y,
  };
};

export const InputHandler = ({ dispatch, gutterWidth, height, pageRanges, scale }: Props) => {
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      dispatch({
        ctrlClick: e.ctrlKey,
        position: computeMousePosition(e, gutterWidth, pageRanges, scale),
        shiftClick: e.shiftKey,
        type: "MOUSE DOWN",
      });
    },
    [dispatch, gutterWidth, pageRanges],
  );
  const style = useMemo(() => {
    return {
      height: `${height}px`,
    };
  }, [height]);
  return <div className="input-handler" onMouseDown={handleMouseDown} style={style} />;
};
